const importantStyles = [
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'text-align',
  'line-height',
  'color',
  'background-color',
  'text-decoration',
  'list-style-type',
];

const applyInlineStyles = (original: HTMLElement, clone: HTMLElement) => {
  const computedStyle = window.getComputedStyle(original);

  importantStyles.forEach((style) => {
    const value = computedStyle.getPropertyValue(style);

    if (value) {
      clone.style.setProperty(style, value, 'important');
    }
  });

  const originalChildren = Array.from(original.children);
  const cloneChildren = Array.from(clone.children);

  originalChildren.forEach((originalChild, index) => {
    const cloneChild = cloneChildren[index];

    if (originalChild instanceof HTMLElement && cloneChild instanceof HTMLElement) {
      applyInlineStyles(originalChild, cloneChild);
    }
  });
};

/**
 * Copy an element's rendered content to the clipboard as both HTML and plain text.
 *
 * Clones the element, applies computed styles inline (so pasted output in Word/Docs/Notion
 * keeps formatting), strips images/svgs/buttons and anything marked `data-copy-exclude`,
 * applies any per-element overrides declared via `ClipboardStyled`, and writes both
 * text/html and text/plain variants.
 */
export const copyElementToClipboard = async (element: HTMLElement): Promise<boolean> => {
  const clone = element.cloneNode(true) as HTMLElement;

  applyInlineStyles(element, clone);

  clone.querySelectorAll('svg, img, button, [data-copy-exclude]').forEach((el) => el.remove());

  // Apply custom copy styles from data-copy-style attributes (see ClipboardStyled)
  clone.querySelectorAll('[data-copy-style]').forEach((cloneEl) => {
    if (cloneEl instanceof HTMLElement) {
      try {
        const copyStyles = JSON.parse(cloneEl.dataset.copyStyle || '{}');

        Object.entries(copyStyles).forEach(([property, value]) => {
          const kebabProperty = property.replace(/([A-Z])/g, '-$1').toLowerCase();

          cloneEl.style.setProperty(kebabProperty, value as string, 'important');
        });
      } catch {
        // Invalid JSON, skip
      }
    }
  });

  // Remove paragraph margins to prevent "space before/after" in Word/Docs
  clone.querySelectorAll('p, div').forEach((el) => {
    if (el instanceof HTMLElement) {
      el.style.setProperty('margin', '0', 'important');
    }
  });

  // innerText only reflects line breaks for an element that is in the document.
  clone.style.setProperty('position', 'fixed');
  clone.style.setProperty('left', '-100000px');
  document.body.appendChild(clone);
  const plainText = clone.innerText;

  clone.remove();

  const htmlContent = `
    <html>
      <body>${clone.innerHTML}</body>
    </html>
  `;

  try {
    const clipboardItem = new ClipboardItem({
      'text/html': new Blob([htmlContent], { type: 'text/html' }),
      'text/plain': new Blob([plainText], { type: 'text/plain' }),
    });

    await navigator.clipboard.write([clipboardItem]);

    return true;
  } catch (err) {
    console.error('copyElementToClipboard: clipboard write failed', err);

    return false;
  }
};
